'use client';

import { useCallback, useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { heroSlides } from '@/lib/heroSlides';
import { ScrollLink } from '@/components/layout/ScrollLink';

const AUTOPLAY_DELAY = 6000;

/* Shared button styling so the CTA renders identically whether it is a
   Link (route) or a ScrollLink (in-page anchor). */
const primaryBtn =
  'group inline-flex items-center gap-2 cursor-pointer bg-white text-[#0a2351] px-6 py-3 md:px-8 md:py-4 rounded-md font-semibold shadow-lg hover:bg-blue-50 transition transform hover:scale-105 active:scale-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white';
const secondaryBtn =
  'inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white px-6 py-3 md:px-8 md:py-4 rounded-md font-medium border border-white/40 hover:bg-white/20 transition transform hover:scale-105 active:scale-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white';

const Cta = ({ cta, className, children }) =>
  cta.scrollTo ? (
    <ScrollLink to={cta.scrollTo} offset={-100} className={className}>
      {cta.label}
      {children}
    </ScrollLink>
  ) : (
    <Link href={cta.href} className={className}>
      {cta.label}
      {children}
    </Link>
  );

const HeroCarousel = () => {
  const prefersReducedMotion = useReducedMotion();

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, duration: 32 }, [
    Autoplay({
      delay: AUTOPLAY_DELAY,
      stopOnMouseEnter: true,
      stopOnInteraction: false,
      stopOnFocusIn: true,
    }),
  ]);

  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    onSelect();
    emblaApi.on('select', onSelect).on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect).off('reInit', onSelect);
    };
  }, [emblaApi]);

  // Honour the OS "reduce motion" setting: stop auto-advancing entirely.
  useEffect(() => {
    if (!emblaApi || !prefersReducedMotion) return;
    emblaApi.plugins()?.autoplay?.stop();
  }, [emblaApi, prefersReducedMotion]);

  const textVariants = {
    hidden: { opacity: 0, y: prefersReducedMotion ? 0 : 24 },
    show: (i) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.55, delay: prefersReducedMotion ? 0 : 0.12 * i, ease: 'easeOut' },
    }),
  };

  return (
    <section
      className="group/hero relative w-full overflow-hidden bg-[#0a2351]"
      aria-roledescription="carousel"
      aria-label="Seternity Solutions highlights"
    >
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex touch-pan-y">
          {heroSlides.map((slide, index) => {
            const isActive = index === selectedIndex;
            return (
              <div
                key={slide.id}
                /* overflow-hidden clips the Ken Burns zoom: the image scales
                   to 1.08 while the scrim stays at inset-0, so without this
                   the extra 4% on each side spills past the scrim and shows
                   as an un-dimmed sliver at the slide edges. */
                className="relative flex-[0_0_100%] min-w-0 overflow-hidden"
                role="group"
                aria-roledescription="slide"
                aria-label={`${index + 1} of ${heroSlides.length}`}
                aria-hidden={!isActive}
              >
                {/* Background photo with a slow Ken Burns drift while active */}
                <motion.div
                  className="absolute inset-0"
                  animate={{ scale: isActive && !prefersReducedMotion ? 1.08 : 1 }}
                  transition={{ duration: 9, ease: 'linear' }}
                >
                  <Image
                    src={slide.image}
                    alt={slide.imageAlt}
                    fill
                    priority={index === 0}
                    fetchPriority={index === 0 ? 'high' : 'auto'}
                    loading={index === 0 ? undefined : 'lazy'}
                    sizes="100vw"
                    quality={80}
                    className="object-cover object-center"
                  />
                </motion.div>

                {/* Scrim: heavy on the left so the copy always stays readable */}
                <div
                  className="absolute inset-0 bg-gradient-to-r from-[#0a2351]/95 via-[#0a2351]/80 to-[#0a2351]/40"
                  aria-hidden="true"
                />
                <div
                  className="absolute inset-0 bg-gradient-to-t from-[#0a2351]/80 via-transparent to-[#0a2351]/50"
                  aria-hidden="true"
                />

                {/* Content */}
                {/* Full viewport height. On md+ the OfferNavbar (~40px) sits
                    above the hero in normal flow, so subtract it to land the
                    hero bottom exactly on the fold. Adjust 40px if that bar
                    changes height. */}
                <div className="relative z-10 max-w-7xl mx-auto px-6 flex items-center min-h-[100svh] md:min-h-[calc(100svh-40px)] pt-28 md:pt-32 pb-24">
                  <div className="max-w-3xl text-white">
                    <motion.div
                      custom={0}
                      variants={textVariants}
                      initial="hidden"
                      animate={isActive ? 'show' : 'hidden'}
                      className="inline-flex items-center gap-2 mb-5 bg-white/15 backdrop-blur px-4 py-2 rounded-full text-xs md:text-sm font-semibold border border-white/30"
                    >
                      <span
                        className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"
                        aria-hidden="true"
                      />
                      {slide.eyebrow}
                    </motion.div>

                    <motion.h1
                      custom={1}
                      variants={textVariants}
                      initial="hidden"
                      animate={isActive ? 'show' : 'hidden'}
                      className="font-main font-bold text-3xl sm:text-5xl lg:text-6xl leading-[1.12] mb-6 drop-shadow-sm"
                    >
                      {slide.title} {slide.titleAccent}
                    </motion.h1>

                    <motion.p
                      custom={2}
                      variants={textVariants}
                      initial="hidden"
                      animate={isActive ? 'show' : 'hidden'}
                      className="text-white/90 text-base md:text-lg max-w-2xl mb-9 leading-relaxed"
                    >
                      {slide.description}
                    </motion.p>

                    <motion.div
                      custom={3}
                      variants={textVariants}
                      initial="hidden"
                      animate={isActive ? 'show' : 'hidden'}
                      className="flex flex-wrap gap-4"
                    >
                      {/* Only the active slide is reachable by keyboard */}
                      <div inert={!isActive}>
                        <Cta cta={slide.primaryCta} className={primaryBtn}>
                          <ArrowRight
                            className="w-4 h-4 transition-transform group-hover:translate-x-1"
                            aria-hidden="true"
                          />
                        </Cta>
                      </div>
                      <div inert={!isActive}>
                        <Cta cta={slide.secondaryCta} className={secondaryBtn} />
                      </div>
                    </motion.div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Controls */}
      <div className="absolute bottom-24 md:bottom-32 left-0 right-0 z-20 pointer-events-none">
        <div className="max-w-7xl mx-auto px-6 flex items-end justify-end gap-6">
          {/* Arrows */}
          <div className="pointer-events-auto hidden sm:flex items-center gap-3">
            <button
              type="button"
              onClick={scrollPrev}
              aria-label="Previous slide"
              className="w-11 h-11 rounded-full border border-white/40 bg-white/10 backdrop-blur-sm text-white flex items-center justify-center hover:bg-white hover:text-[#0a2351] transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              <ChevronLeft className="w-5 h-5" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={scrollNext}
              aria-label="Next slide"
              className="w-11 h-11 rounded-full border border-white/40 bg-white/10 backdrop-blur-sm text-white flex items-center justify-center hover:bg-white hover:text-[#0a2351] transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              <ChevronRight className="w-5 h-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroCarousel;
